import { create } from "zustand";
import { persist } from "zustand/middleware";
import AuthData from "../authUser/auth/entities/AuthData";
import FirstLoginPasswordResetContext from "../authUser/auth/entities/FirstLoginPasswordResetContext";
import Roles from "../authUser/auth/entities/Roles";
import { canMatch } from "../authUser/auth/services/PermissionService";
import { useMenuStore } from "./menuStore";

// Dynamically imported in logout() below: these stores eagerly resolve
// repositories from the DI container at module scope, and the DI container's
// own HttpClient imports this file to log out on 401 — a static import here
// would form a require cycle that crashes on module load.
const resetOnboardingRelatedStores = () => {
  import("./onboarding/onboardingFormStore").then(({ useOnboardingFormStore }) =>
    useOnboardingFormStore.getState().resetForm(),
  );
  import("./customers/screeningStore").then(({ useScreeningStore }) =>
    useScreeningStore.getState().resetState(),
  );
  import("./customers/documentUploadStore").then(({ useDocumentUploadStore }) =>
    useDocumentUploadStore.getState().resetState(),
  );
};

export interface AuthState {
  // State
  user: AuthData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isPersistent: boolean;
  /** In-memory only: user must complete password reset before a real session exists */
  firstLoginPasswordReset: FirstLoginPasswordResetContext | null;

  // Actions
  login: (authData: AuthData) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  enablePersistence: () => void;
  disablePersistence: () => void;
  setFirstLoginPasswordReset: (
    context: FirstLoginPasswordResetContext | null,
  ) => void;

  // Session management methods
  checkSession: () => boolean;

  // Computed values
  hasRole: (role: Roles) => boolean;
  /**
   * Checks a raw Keycloak role name (`roleLabels`), not the `Roles` enum.
   * `roles` is never populated by the login mapper today (always `[]`, see
   * AuthDataMapper). `hasRole` above is effectively dead code. Kept separate
   * rather than fixing that mapping, which is a broader change than this
   * gate needs.
   */
  hasRoleLabel: (label: string) => boolean;
  hasPermission: (permission: string) => boolean;
  can: (domain: string, action: string, resource: string) => boolean;

  getToken: () => string | null;
  getHabilite: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isPersistent: false,
      firstLoginPasswordReset: null,

      // Actions
      login: (authData: AuthData) => {
        set({
          user: authData,
          isAuthenticated: true,
          error: null,
          isLoading: false,
          firstLoginPasswordReset: null,
        });
        // Rebuild menu immediately so RBAC uses the new session (Sidebar may not re-run yet).
        const menu = useMenuStore.getState();
        menu.buildMenu(authData);
        menu.buildFooterMenu(authData);
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false,
          isPersistent: false,
          firstLoginPasswordReset: null,
        });
        useMenuStore.getState().resetMenuForSession();
        resetOnboardingRelatedStores();
      },

      setFirstLoginPasswordReset: (context) => {
        set({ firstLoginPasswordReset: context });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error, isLoading: false });
      },

      clearError: () => {
        set({ error: null });
      },

      enablePersistence: () => {
        set({ isPersistent: true });
      },

      disablePersistence: () => {
        set({ isPersistent: false });
      },

      // Session management methods
      checkSession: () => {
        const { user, isAuthenticated } = get();
        const token = user?.token;
        return (
          isAuthenticated &&
          !!user &&
          typeof token === "string" &&
          token.length > 0
        );
      },

      // Computed values
      hasRole: (role: Roles) => {
        const { user } = get();
        return Array.isArray(user?.roles) ? user!.roles.includes(role) : false;
      },

      hasRoleLabel: (label: string) => {
        const { user } = get();
        return Array.isArray(user?.roleLabels)
          ? user?.roleLabels.includes(label)
          : false;
      },

      hasPermission: () => {
        const { user } = get();
        return Array.isArray(user?.roles)
          ? user!.roles.includes(Roles.ADMIN)
          : false;
      },

      can: (domain, action, resource) => {
        const { user } = get();
        if (!user) return false;
        if (canMatch(user.effectivePermissions, domain, action, resource))
          return true;
        return Array.isArray(user.roles) && user.roles.includes(Roles.ADMIN);
      },

      getToken: () => {
        const { user } = get();
        const t = user?.token;
        return typeof t === "string" && t.length > 0 ? t : null;
      },

      getHabilite: () => get().user?.habilite === true,
    }),
    {
      name: "auth-storage",
      partialize: (state) => {
        if (state.isPersistent) {
          return {
            user: state.user,
            isAuthenticated: state.isAuthenticated,
            isPersistent: state.isPersistent,
          };
        } else {
          return {
            isPersistent: state.isPersistent,
          };
        }
      },
      onRehydrateStorage: () => (state) => {
        // If we're rehydrating and persistence is disabled, clear user data
        if (state && !state.isPersistent) {
          state.user = null;
          state.isAuthenticated = false;
          state.firstLoginPasswordReset = null;
        }
      },
    },
  ),
);
